import React from "react"
import { Employee } from "../../Objects/Employee/Employee"
import { ButtonIcon } from "../Button/Button"
import { faArrowRightFromBracket } from "@fortawesome/free-solid-svg-icons"
import { useEmployeeContext } from "../../context/useMenuEmployeeContext"
import { useLoadImage } from "../../hooks/useLoadImage"
import { useLogout } from "../../hooks/useLogout"
import Animate, { AnimationTypes } from "../Animate/Animate"
import { useTranslation } from "react-i18next"
import { SIZE } from "../../configuration/Sizes"
import THEME from "../../configuration/Theme"
import { getEmployeeAvatar } from "../../utils/employeeAvatar"

interface EmployeeCardProps {
  employee: Employee
  onEventClick?: () => void
}

export const EmployeeCard: React.FC<EmployeeCardProps> = ({ employee, onEventClick }) => {
  const { t } = useTranslation("employee")

  const avatarUrl = getEmployeeAvatar(employee.iconUrl, employee.isMaster)

  const isMasterEmployee = employee.isMaster

  const { loading, errorOccurred, handleErrorLoad, handleLoad } = useLoadImage(isMasterEmployee ? "" : avatarUrl)

  const { setEmployee, handleOpenEmployee, setTitle } = useEmployeeContext()

  const { handleLogOut } = useLogout()

  const handleClickEnableEmployee = () => {
    if (employee.isMaster) {
      return
    }

    setEmployee(employee)

    handleOpenEmployee()

    setTitle(t("title_popup_edite"))

    if (onEventClick !== null) {
      onEventClick()
    }
  }

  return (
    <Animate type={AnimationTypes.fadeInTraslate}>
      <div
        className="
          bg-white
          shadow-sm
          hover:shadow-md
          transition-all duration-300
          flex items-center justify-between
          px-2 py-0
        "
      >
        <div
          onClick={handleClickEnableEmployee}
          className={`flex items-center gap-4 flex-1 min-w-0 ${employee.isMaster ? "cursor-default" : "cursor-pointer"}`}
          data-interaction-description-key="interactionDescriptions:components.employeeCard.employeeCard.button.handleClickEnableEmployee"
        >
          <div className="relative w-[65px] h-[65px] shrink-0">
            {!isMasterEmployee && !errorOccurred && loading && (
              <div className="w-[65px] h-[65px] rounded-full border-4 border-slate-200 border-t-red-600 animate-spin" />
            )}

            {isMasterEmployee && (
              <img
                src={avatarUrl}
                alt={employee.personEntity.name}
                className="
                  w-[65px] h-[65px]
                  object-cover
                  rounded-full
                  p-3
                  transition-transform duration-300
                "
              />
            )}

            {!isMasterEmployee && !errorOccurred && avatarUrl.length > 0 && (
              <img
                src={avatarUrl}
                alt={employee.personEntity.name}
                onLoad={handleLoad}
                onError={handleErrorLoad}
                className="
                  w-[65px] h-[65px]
                  object-cover
                  rounded-full
                  transition-transform duration-300
                  hover:scale-105
                "
              />
            )}

            {errorOccurred && !isMasterEmployee && (
              <img
                src="https://cdn.neemo.com.br/uploads/settings_webdelivery/logo/1209/nao-perfil.gif"
                className="w-[65px] h-[65px] object-cover rounded-full"
              />
            )}
          </div>

          <div className="flex min-w-0 flex-col">
            <h3 className="truncate text-[14px] font-semibold text-slate-900">{employee.personEntity.name}</h3>

            <h4 className="truncate text-sm text-slate-500">{employee.companyFunction}</h4>
          </div>
        </div>

        <div className="shrink-0">
          <ButtonIcon
            sizeNumber={SIZE.buttonExpandIcon}
            bgColor={THEME.colors.buttonBGCloseColor}
            iconColor={THEME.colors.buttonFGCloseColor}
            icon={faArrowRightFromBracket}
            onClick={handleLogOut}
            size="small"
            descriptionKey="interactionDescriptions:components.employeeCard.employeeCard.button.handleLogOut"
          />
        </div>
      </div>
    </Animate>
  )
}
